import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { getAllTeamMembers } from "../../services/teamServices";
import { MdPhone, MdMessage, MdEmail } from "react-icons/md";
import { FiX } from "react-icons/fi";
import SectionTitle from "../../components/ui/SectionTitle";
import Button from "./../../components/Button/Button";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const colorList = [
  "border-l-blue-500",
  "border-l-orange-500",
  "border-l-emerald-500",
  "border-l-yellow-400",
  "border-l-purple-500",
  "border-l-pink-500",
  "border-l-cyan-500",
  "border-l-red-500",
];

const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, "")
    .trim();

const hasDetails = (m) =>
  Boolean(
    (m?.bio_member && stripHtml(m.bio_member)) ||
    m?.tel_member ||
    m?.mail_member,
  );

const safeBio = (html) =>
  DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    FORBID_ATTR: ["style"],
  });

function TeamCard({ member, onOpen, accentClass, indexInCat }) {
  const photo = toAbsolute(member.photoURL) || "/img/user_avatar.jpg";
  const details = hasDetails(member);

  return (
    <article
      className={cn(
        "group relative overflow-hidden rounded-3xl",
        "border border-slate-200/70 dark:border-slate-800",
        "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
        "shadow-sm hover:shadow-xl hover:-translate-y-[3px]",
        "transition duration-300 motion-reduce:transition-none",
        "motion-safe:animate-[teamIn_.55s_ease_both]",
      )}
      style={{
        animationDelay: `${Math.min(indexInCat, 10) * 60}ms`,
      }}
    >
      {/* Shine anim au hover */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -left-20 -top-24 h-40 w-40 rotate-12",
          "bg-white/40 dark:bg-white/10 blur-xl",
          "opacity-0 group-hover:opacity-100",
          "transition duration-300",
          "motion-safe:group-hover:animate-[teamShine_1.2s_ease-in-out]",
        )}
      />

      {/* Photo grand format */}
      <div className="relative">
        <div className="aspect-square w-full overflow-hidden">
          <img
            src={photo}
            alt={`${member.nom} ${member.prenom}`}
            loading="lazy"
            decoding="async"
            className={cn(
              "h-full w-full object-cover",
              "transition duration-500 group-hover:scale-[1.06] motion-reduce:transition-none",
            )}
          />
        </div>

        {/* Overlay gradient (lisibilité) */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent"
        />

        {/* Badge fonction (sur la photo) */}
        {/* {member.fonction ? (
          <div className="absolute bottom-3 left-3 right-3">
            <div
              className={cn(
                "inline-flex max-w-full items-center justify-center",
                "rounded-full px-3 py-1",
                "bg-white/85 dark:bg-slate-950/70 backdrop-blur",
                "text-xs font-extrabold text-primary",
                "ring-1 ring-primary/20",
              )}
              title={member.fonction}
            >
              <span className="truncate">{member.fonction}</span>
            </div>
          </div>
        ) : null} */}
      </div>

      {/* Texte sous photo */}
      <div className="p-4 text-center">
        <div className="text-sm font-extrabold uppercase tracking-wide text-slate-900 dark:text-white truncate">
          {member.nom}
        </div>
        <div className="text-sm text-slate-700 dark:text-slate-200 truncate">
          {member.prenom}
        </div>

        {member.fonction ? (
          <div
            className={cn(
              "mt-2 inline-flex max-w-full items-center justify-center",
              "rounded-xl px-3 py-1",
              "text-xs font-semibold italic",
              "bg-slate-100 text-slate-700",
              "dark:bg-slate-800/70 dark:text-slate-200",
              "ring-1 ring-slate-200/70 dark:ring-slate-700/60",
            )}
            title={member.fonction}
          >
            <span className="truncate">{member.fonction}</span>
          </div>
        ) : null}

        {/* Bouton Détails uniquement si infos */}
        {details ? (
          <button
            onClick={() => onOpen(member)}
            className={cn(
              "mt-3 inline-flex items-center justify-center",
              "rounded-full px-4 py-2",
              "text-sm font-semibold",
              "border border-primary/30",
              "text-primary",
              "bg-primary/10 hover:bg-primary/15",
              "transition",
            )}
          >
            Détails
          </button>
        ) : null}
      </div>

      {/* Accent discret (bordure bas) */}
      <div
        aria-hidden
        className={cn(
          "h-1.5 w-full bg-gradient-to-r from-primary/0 via-primary/45 to-primary/0",
          "opacity-60 group-hover:opacity-100 transition",
        )}
      />

      {/* Keyframes locales */}
      <style>{`
        @keyframes teamIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes teamShine {
          0%   { transform: translateX(-30px) translateY(-30px) rotate(12deg); }
          100% { transform: translateX(260px) translateY(260px) rotate(12deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .motion-safe\\:animate-\\[teamIn_.55s_ease_both\\] { animation: none !important; }
        }
      `}</style>
    </article>
  );
}

function ProfileModal({ member, onClose, borderClass }) {
  const photo = toAbsolute(member.photoURL) || "/img/user_avatar.jpg";
  const bio = safeBio(member.bio_member);

  const call = () =>
    member.tel_member && window.open(`tel:${member.tel_member}`, "_blank");
  const sms = () =>
    member.tel_member && window.open(`sms:${member.tel_member}`, "_blank");
  const email = () =>
    member.mail_member && window.open(`mailto:${member.mail_member}`, "_blank");

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-3"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          "relative w-full max-w-3xl overflow-hidden rounded-3xl",
          "bg-white/90 dark:bg-slate-950/80 backdrop-blur",
          "border border-slate-200/60 dark:border-slate-800/70",
          "shadow-2xl",
          "border-l-8",
          borderClass,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={cn(
            "absolute top-4 right-4 z-10",
            "h-10 w-10 rounded-full",
            "bg-white/70 dark:bg-slate-900/60",
            "border border-slate-200/70 dark:border-slate-800",
            "grid place-items-center",
            "text-slate-700 dark:text-slate-200 hover:text-primary transition",
          )}
          onClick={onClose}
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        <div className="p-6 sm:p-10">
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
            <div className="w-full sm:w-[260px]">
              <div className="relative overflow-hidden rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-900/60">
                <img
                  src={photo}
                  alt={`${member.nom} ${member.prenom}`}
                  className="w-full h-[280px] object-cover"
                  loading="lazy"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                {member.categorie || "Membre"}
              </div>

              <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                <span className="uppercase">{member.nom}</span> {member.prenom}
              </h3>

              {member.fonction ? (
                <p className="mt-2 text-sm font-semibold text-primary">
                  {member.fonction}
                </p>
              ) : null}

              <div className="mt-5 flex flex-wrap gap-3">
                {member.tel_member ? (
                  <>
                    <Button
                      text=""
                      icon={MdPhone}
                      hoverLabel="Appeler"
                      onClick={call}
                    />
                    <Button
                      text=""
                      icon={MdMessage}
                      hoverLabel="Envoyer un SMS"
                      onClick={sms}
                    />
                  </>
                ) : null}
                {member.mail_member ? (
                  <Button
                    text=""
                    icon={MdEmail}
                    hoverLabel="Envoyer un email"
                    onClick={email}
                  />
                ) : null}
              </div>

              <div className="mt-6 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                {member.mail_member ? (
                  <p>
                    <span className="font-semibold">Email :</span>{" "}
                    <a
                      className="text-primary hover:underline"
                      href={`mailto:${member.mail_member}`}
                    >
                      {member.mail_member}
                    </a>
                  </p>
                ) : null}

                {member.tel_member ? (
                  <p>
                    <span className="font-semibold">Téléphone :</span>{" "}
                    {member.tel_member}
                  </p>
                ) : null}

                {member.bio_member ? (
                  <div>
                    <p className="font-semibold mb-2">Bio</p>
                    <div
                      className={cn(
                        "prose prose-slate dark:prose-invert max-w-none",
                        "rounded-2xl border border-slate-200/60 dark:border-slate-800/60",
                        "bg-slate-50/70 dark:bg-slate-900/40 p-4",
                        // ✅ gras blanc en dark
                        "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                        "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold",
                      )}
                      dangerouslySetInnerHTML={{ __html: bio }}
                    />
                  </div>
                ) : null}
              </div>

              <div className="mt-8">
                <Button text="Fermer" onClick={onClose} variant="outline" />
              </div>
            </div>
          </div>
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200/80 dark:via-slate-800 to-transparent" />
      </div>
    </div>
  );
}

const NosEquipes = () => {
  const [team, setTeam] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    (async () => {
      const data = await getAllTeamMembers();
      setTeam(Array.isArray(data) ? data : []);
    })();
  }, []);

  // ✅ Grouping en conservant l’ordre d’origine (catégories + membres)
  const groupedEntries = useMemo(() => {
    const map = new Map();
    (team || []).forEach((m) => {
      const cat = m?.categorie?.trim() || "Autres";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(m); // ordre API conservé
    });
    return Array.from(map.entries());
  }, [team]);

  // Border class de la modale en fonction de la catégorie (ordre d’apparition)
  const categoryNames = groupedEntries.map(([cat]) => cat);
  const modalCatIndex = categoryNames.indexOf(
    selectedMember?.categorie?.trim() || "Autres",
  );
  const modalBorderClass =
    colorList[(modalCatIndex >= 0 ? modalCatIndex : 0) % colorList.length];

  const openMember = (m) => {
    if (hasDetails(m)) setSelectedMember(m); // ✅ ouvre seulement s'il y a des infos
  };

  return (
    <section
      id="nosEquipes"
      className={cn(
        "relative py-12 sm:py-16",
        "bg-lightBackground dark:bg-darkBackground",
        "text-slate-900 dark:text-slate-50",
      )}
    >
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0",
          "bg-[radial-gradient(900px_circle_at_20%_10%,rgba(99,102,241,0.10),transparent_55%),radial-gradient(700px_circle_at_90%_30%,rgba(236,72,153,0.06),transparent_60%)]",
        )}
      />

      <div className="relative mx-auto max-w-7xl px-4">
        <SectionTitle
          title="NOS ÉQUIPES"
          subtitle="Découvrez les membres qui font vivre le club au quotidien."
        />

        {/* Toutes les catégories + tous les membres (pas de clic “suite”) */}
        <div className="mt-10 space-y-10">
          {groupedEntries.map(([categorie, membres], idxCat) => (
            <div
              key={categorie}
              className={cn(
                "rounded-3xl",
                "border border-slate-200/60 dark:border-slate-800/70",
                "bg-white/60 dark:bg-slate-900/40 backdrop-blur",
                "shadow-sm overflow-hidden",
              )}
            >
              {/* Header catégorie */}
              <div
                className={cn(
                  "border-l-8 px-5 sm:px-7 py-5",
                  colorList[idxCat % colorList.length],
                  "bg-gradient-to-r from-slate-50/80 to-transparent dark:from-slate-950/30",
                )}
              >
                <div className="flex items-center gap-4">
                  <h3 className="text-lg sm:text-xl font-extrabold uppercase tracking-wide">
                    {categorie}
                  </h3>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    ({membres.length})
                  </span>
                  <span className="hidden sm:block h-px flex-1 bg-slate-200/80 dark:bg-slate-800" />
                </div>
              </div>

              {/* Grid cards */}
              <div className="p-5 sm:p-7">
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-5 xl:grid-cols-6">
                  {membres.map((member, i) => (
                    <TeamCard
                      key={member.id ?? `${member.nom}-${member.prenom}-${i}`}
                      member={member}
                      onOpen={openMember}
                      accentClass={colorList[idxCat % colorList.length]}
                      indexInCat={i}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedMember ? (
        <ProfileModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          borderClass={modalBorderClass}
        />
      ) : null}
    </section>
  );
};

export default NosEquipes;
